<template>
  <main class="file-info">
    <section class="info">
      <table>
        <thead>
          <tr>
            <th abbr="details about file" class="info__heading">
              {{ t('general__text__details') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ t('general__text__uploaded') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="uploadDate"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__uploaded-by') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="uploadedBy"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__placement') }}</td>
            <td>
              <DirectoryItemField :item="directoryItem" field="entityConnection" />
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    <br />
    <section class="info">
      <table>
        <thead>
          <tr>
            <th abbr="file information, e.g type, size etc" class="info__heading">
              {{ t('general__data__file-information') }}
            </th>
          </tr>
        </thead>
        <!-- mimeType, fileSize and lastModified for both docs and imgs -->
        <tbody>
          <tr>
            <td>{{ t('general__text__file-type') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="type"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__size') }}</td>
            <td>
              <DirectoryItemField :item="directoryItem" field="fileSize" />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.lastModified">
            <td>{{ t('general__text__last-modified-date') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="lastModified"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <!-- doc specific -->
          <tr v-if="directoryItem.metadata?.lastAuthor">
            <td>
              {{ `${t('general__text__last-modified')} ${t('general__text__by').toLowerCase()}` }}
            </td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="lastAuthor"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.createdDate">
            <td>{{ t('general__text__created-date') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="createdDate"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.creator">
            <td>
              {{ `${t('general__text__created')} ${t('general__text__by').toLowerCase()}` }}
            </td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="creator"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.nrOfPages">
            <td>{{ t('general__text__pages-number-of') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="nrOfPages"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <!-- img specific -->
          <tr v-if="directoryItem.metadata?.lastModified">
            <td>{{ t('general__text__last-modified-date') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="lastModified"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.width">
            <td>{{ t('general__text__width') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="width"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.length">
            <td>{{ t('general__data__length') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="length"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <tr v-if="directoryItem.metadata?.height">
            <td>{{ t('general__text__height') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="height"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
          <!-- due to serialization issues on backend, check for both lower and upper case R -->
          <tr
            v-if="
              (directoryItem.metadata?.xresolution || directoryItem.metadata?.xResolution) &&
              (directoryItem.metadata?.yResolution || directoryItem.metadata?.yresolution)
            "
          >
            <td>{{ t('general__text__dimensions') }}</td>
            <td>
              <DirectoryItemField
                :item="directoryItem"
                field="resolution"
                :options="{ type: 'tiny', weight: 'bold' }"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </main>
</template>
<script setup lang="ts">
import { toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { DirectoryItem } from './types'
import DirectoryItemField from './DirectoryItemField.vue'

type Props = {
  directoryItem: DirectoryItem<'file'>
}
const props = withDefaults(defineProps<Props>(), {
  directoryItem: undefined
})
const { directoryItem } = toRefs(props)
const { t } = useI18n()
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';
.file-info {
  align-items: center;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  @include mixins.scrollbar-vertical;

  .info {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    &__heading {
      color: variables.$color-greyscale-content;
      @include font-mixins.desktop-bold-label;
      padding: 8px 4px;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      display: block;
      tr {
        display: flex;
        justify-content: space-between;
        align-items: center;
        &:not(:last-child) {
          border-bottom: 0.5px solid variables.$color-greyscale-line-grey;
        }
        td {
          color: variables.$color-greyscale-content;
          padding: 8px 4px;
          flex-grow: inherit;
          @include font-mixins.desktop-bold-tiny;
          &:first-child {
            color: variables.$color-greyscale-secondary;
            width: 121px;
            @include font-mixins.desktop-regular-tiny;
          }
        }
      }
    }
  }
}
</style>
